import React, { useState, useRef, useEffect, useCallback, useMemo } from 'react';
import type { ComponentContentSchema, SlotGroupDefinition } from '@vibesites/runtime';
import {
  useContentToken,
  useSlotGroup,
  useProperty,
  usePropertyField,
  useCollectionBinding,
} from '@vibesites/runtime/react';
import { EditableToken, EditableSlotGroup } from '@vibesites/runtime/react';
import pagesData from '../pages.json';
import { VibesitesImage } from '@vibesites/ui-primitives';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import {
  faFacebook,
  faXTwitter,
  faGoogle,
} from '@fortawesome/free-brands-svg-icons';
// Icons that pair with the utility-row quick links. `faUsers` reads as
// "group of residents" for the Residents link (matching the reference
// demo's two-person `Residents-1920w.png` glyph at
// https://oasis-demo-theme.multiscreensite.com/contact-us). `faHandshake`
// reads as "agreement / sign on" for Apply Now — an explicit override
// of the reference's lease-document glyph, requested so the action
// frames the lease as an agreement rather than paperwork. Pulled from
// the already-bundled `free-solid-svg-icons` package so no new
// dependency is introduced.
import { faUsers, faHandshake } from '@fortawesome/free-solid-svg-icons';

// ============================================================================
// Dynamic Navigation from pages.json
// ============================================================================

interface PageEntry {
  name: string;
  path: string;
  position: number;
  visible?: boolean;
  template?: string;
  noindex?: boolean;
  /** When set, this page is rendered as a sub-nav item under the parent
      page's nav entry (a dropdown on desktop, indented row on mobile). */
  parent?: string;
}

/** Page aliases that should never appear in the main navigation */
const NAV_EXCLUDED_ALIASES = new Set([
  'home',
  'privacy-policy',
  'sitemap',
  'accessibility',
  'apply-now',
  'thank-you',
  'resident-access',
  'schedule-a-tour',
  'hyperlocal',
  'reviews',
]);

interface NavLink {
  label: string;
  href: string;
  /** Sub-nav items rendered as a dropdown on desktop and indented rows on
      mobile. Populated when other pages declare `parent: <this alias>`. */
  children?: NavLink[];
}

interface VirtualTour {
  virtual_tour_url?: string | null;
}

/**
 * Build navigation links from pages.json.
 * Only includes pages that are visible and not in the exclusion list.
 * Sorted by position. Pages declaring `parent: <alias>` are nested as
 * `children` of their parent entry rather than appearing at the top level.
 * Orphaned children (parent missing or hidden) fall back to the top level
 * so a page is never silently dropped from the nav.
 *
 * `hasInteractiveLeasing` controls the dynamic visibility of the
 * Interactive Map page — that page renders the ApartmentSync (Repli360 /
 * RRAC) "interactive leasing view" widget, which is keyed by two per-
 * property custom fields: `custom_apartmentsync_javascript_id` (the
 * encoded token in the script URL) and `custom_apartmentsync_config_id`
 * (the site identifier). Both are declared as required-style tokens on
 * the apartment-sync-map block, and a property without both is a
 * half-configured / no-real-map state (Centre Point being the canonical
 * example — it has neither, but earlier versions of this check let it
 * leak into the nav when only one ID was populated as a placeholder).
 * If either is empty we drop the page from the nav (the SSG also marks
 * the page hidden + noindex so it isn't crawled — see ssg-entry.ts).
 */
function buildNavLinks(hasVirtualTours: boolean, hasInteractiveLeasing: boolean): NavLink[] {
  const eligible = Object.entries(pagesData as Record<string, PageEntry>)
    .filter(([alias, page]) => {
      if (NAV_EXCLUDED_ALIASES.has(alias)) return false;
      if (page.visible === false) return false;
      if (page.template) return false;
      if (alias === 'virtual-tour' && !hasVirtualTours) return false;
      if (alias === 'interactive-map' && !hasInteractiveLeasing) return false;
      return true;
    })
    .sort(([, a], [, b]) => a.position - b.position);

  const eligibleAliases = new Set(eligible.map(([alias]) => alias));
  const linksByAlias = new Map<string, NavLink>();
  const topLevel: NavLink[] = [];

  // First pass: create a NavLink for every eligible page.
  for (const [alias, page] of eligible) {
    linksByAlias.set(alias, { label: page.name, href: page.path });
  }

  // Second pass: attach children to parents, preserving sort order. Pages
  // whose parent isn't eligible (hidden / excluded / missing) bubble up to
  // the top level so we never lose a page from the nav.
  for (const [alias, page] of eligible) {
    const link = linksByAlias.get(alias)!;
    if (page.parent && eligibleAliases.has(page.parent) && page.parent !== alias) {
      const parentLink = linksByAlias.get(page.parent)!;
      (parentLink.children ??= []).push(link);
    } else {
      topLevel.push(link);
    }
  }

  return topLevel;
}

// ============================================================================
// Slot Group Definitions
// ============================================================================

const logoSlotDef: SlotGroupDefinition = {
  label: 'Logo',
  fields: {
    image: { label: 'Logo Image', type: 'image', defaultField: 'property_mono_black_logo', suggestedFields: ['property_mono_black_logo', 'property_primary_logo', 'property_secondary_logo'] },
    alt: { label: 'Alt Text', type: 'text', defaultValue: 'Community Logo' },
  },
};

const applyNowLinkDef: SlotGroupDefinition = {
  label: 'Apply Now Link',
  fields: {
    url: {
      label: 'URL',
      type: 'url',
      defaultField: 'property_apply_now_url',
      suggestedFields: ['property_apply_now_url', 'property_website_url'],
    },
    label: { label: 'Link Text', type: 'text', defaultValue: 'Apply Now' },
    _target: { label: 'Target', type: 'text', defaultValue: '_self' },
  },
};

// NOTE: The slot group path stays `header.resident_access_link` (and the URL
// stays `/resident-access`) so previously-saved content keeps mapping to the
// same field. The visible label was shortened from "Resident Access" to
// "Residents" to match the reference demo's terser utility-row label.
const residentAccessLinkDef: SlotGroupDefinition = {
  label: 'Residents Link',
  fields: {
    url: { label: 'URL', type: 'url', defaultValue: '/resident-access' },
    label: { label: 'Link Text', type: 'text', defaultValue: 'Residents' },
    _target: { label: 'Target', type: 'text', defaultValue: '_self' },
  },
};

const scheduleTourLinkDef: SlotGroupDefinition = {
  label: 'Schedule Tour Link',
  fields: {
    url: { label: 'URL', type: 'url', defaultValue: '/schedule-a-tour' },
    label: { label: 'Link Text', type: 'text', defaultValue: 'Schedule a Tour' },
    _target: { label: 'Target', type: 'text', defaultValue: '_self' },
  },
};

// Address row inside the mobile drawer. Promoted from a bare
// `header.full_address` content token + auto-generated Google Maps
// directions URL to a slot group so editors can pick the address text,
// the destination URL, and the link target independently — rather than
// having the URL forced to the property's `google_place_url` (or a
// synthesized `/maps/dir/?destination=...` fallback) with no override.
const addressLinkDef: SlotGroupDefinition = {
  label: 'Address Link',
  fields: {
    address: {
      label: 'Address',
      type: 'text',
      defaultField: 'property_full_address',
      defaultValue: '123 Main Street, Atlanta, GA 30313',
    },
    url: {
      label: 'URL',
      type: 'url',
      defaultField: 'property_google_place_url',
      suggestedFields: ['property_google_place_url'],
    },
    _target: { label: 'Target', type: 'text', defaultValue: '_blank' },
  },
};

// View Floor Plans CTA inside the mobile drawer. Hard-coded `/floor-plans`
// before; promoting to a slot group so the editor can retarget the
// button (e.g. to an off-site availability widget) and relabel it. Mirrors
// the schedule-tour link shape so the editor experience is consistent.
const viewFloorPlansLinkDef: SlotGroupDefinition = {
  label: 'View Floor Plans Link',
  fields: {
    url: { label: 'URL', type: 'url', defaultValue: '/floor-plans' },
    label: { label: 'Link Text', type: 'text', defaultValue: 'View Floor Plans' },
    _target: { label: 'Target', type: 'text', defaultValue: '_self' },
  },
};

// Social icon links that live in the utility row, paired with the phone
// on the left. We expose three (Facebook, Twitter / X, Google Business) —
// the most common community-facing channels. URLs default-bind to the
// matching property fields so most properties light up automatically; each
// icon hides itself when its URL is empty so the row stays clean for
// properties that don't run that channel.
const headerFacebookLinkDef: SlotGroupDefinition = {
  label: 'Facebook',
  fields: {
    url: {
      label: 'URL',
      type: 'url',
      defaultField: 'property_facebook_url',
      suggestedFields: ['property_facebook_url'],
    },
    label: { label: 'Label', type: 'text', defaultValue: 'Facebook' },
    _target: { label: 'Target', type: 'text', defaultValue: '_blank' },
  },
};

const headerTwitterLinkDef: SlotGroupDefinition = {
  label: 'Twitter / X',
  fields: {
    url: {
      label: 'URL',
      type: 'url',
      defaultField: 'property_twitter_url',
      suggestedFields: ['property_twitter_url'],
    },
    label: { label: 'Label', type: 'text', defaultValue: 'Twitter' },
    _target: { label: 'Target', type: 'text', defaultValue: '_blank' },
  },
};

const headerGoogleLinkDef: SlotGroupDefinition = {
  label: 'Google Business',
  fields: {
    url: {
      label: 'URL',
      type: 'url',
      defaultField: 'property_google_business_url',
      suggestedFields: ['property_google_business_url'],
    },
    label: { label: 'Label', type: 'text', defaultValue: 'Google' },
    _target: { label: 'Target', type: 'text', defaultValue: '_blank' },
  },
};

// ============================================================================
// SVG Icon Components
// ============================================================================

function PhoneIcon({ className }: { className?: string }) {
  return (
    <svg className={className} fill="none" stroke="currentColor" viewBox="0 0 24 24" aria-hidden="true">
      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M3 5a2 2 0 012-2h3.28a1 1 0 01.948.684l1.498 4.493a1 1 0 01-.502 1.21l-2.257 1.13a11.042 11.042 0 005.516 5.516l1.13-2.257a1 1 0 011.21-.502l4.493 1.498a1 1 0 01.684.949V19a2 2 0 01-2 2h-1C9.716 21 3 14.284 3 6V5z" />
    </svg>
  );
}

function MapPinIcon({ className }: { className?: string }) {
  return (
    <svg className={className} fill="none" stroke="currentColor" viewBox="0 0 24 24" aria-hidden="true">
      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M17.657 16.657L13.414 20.9a1.998 1.998 0 01-2.827 0l-4.244-4.243a8 8 0 1111.314 0z" />
      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 11a3 3 0 11-6 0 3 3 0 016 0z" />
    </svg>
  );
}

function formatPhone(raw: string | null | undefined): string {
  if (!raw) return '';
  const digits = raw.replace(/\D/g, '');
  const d = digits.length === 11 && digits.startsWith('1') ? digits.slice(1) : digits;
  if (d.length !== 10) return raw;
  return `(${d.slice(0, 3)}) ${d.slice(3, 6)}-${d.slice(6)}`;
}

// ============================================================================
// Chevron Down Icon for "More" dropdown
// ============================================================================

function ChevronDownIcon({ className }: { className?: string }) {
  return (
    <svg className={className} fill="none" stroke="currentColor" strokeWidth={2.5} viewBox="0 0 24 24" aria-hidden="true">
      <path strokeLinecap="round" strokeLinejoin="round" d="M19.5 8.25l-7.5 7.5-7.5-7.5" />
    </svg>
  );
}

// ============================================================================
// Desktop Nav Link
// ============================================================================

function DesktopNavLink({ link }: { link: NavLink }) {
  const hasChildren = !!(link.children && link.children.length);
  const [open, setOpen] = useState(false);
  const wrapperRef = useRef<HTMLDivElement>(null);

  // Click-outside / Escape close — mirrors the "More" dropdown so the menu
  // dismisses cleanly when focus or pointer leaves the parent group.
  useEffect(() => {
    if (!open) return;
    function handleClickOutside(e: MouseEvent) {
      if (wrapperRef.current && !wrapperRef.current.contains(e.target as Node)) {
        setOpen(false);
      }
    }
    function handleEscape(e: KeyboardEvent) {
      if (e.key === 'Escape') setOpen(false);
    }
    document.addEventListener('mousedown', handleClickOutside);
    document.addEventListener('keydown', handleEscape);
    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
      document.removeEventListener('keydown', handleEscape);
    };
  }, [open]);

  if (!hasChildren) {
    return (
      <a
        href={link.href}
        className="oasis-nav-darken-hover font-body text-[13px] font-semibold tracking-wider uppercase text-[var(--vibe-color-text)] transition-colors py-2 whitespace-nowrap"
      >
        {link.label}
      </a>
    );
  }

  return (
    <div
      ref={wrapperRef}
      className="relative"
      onMouseEnter={() => setOpen(true)}
      onMouseLeave={() => setOpen(false)}
    >
      {/* The parent label is still a real link so clicking it navigates to
          the parent page; the chevron + dropdown surface the child pages on
          hover/focus without stealing the primary affordance. */}
      <a
        href={link.href}
        className="oasis-nav-darken-hover font-body text-[13px] font-semibold tracking-wider uppercase text-[var(--vibe-color-text)] transition-colors py-2 flex items-center gap-1 whitespace-nowrap"
        aria-haspopup="true"
        aria-expanded={open}
        onFocus={() => setOpen(true)}
      >
        {link.label}
        <ChevronDownIcon
          className={`w-3.5 h-3.5 transition-transform duration-200 ${open ? 'rotate-180' : ''}`}
        />
      </a>

      <div
        className={`nav-more-dropdown absolute left-0 top-full mt-1 border border-[var(--vibe-color-border)] shadow-lg min-w-[200px] z-[70] transition-all duration-200 ${
          open
            ? 'opacity-100 visible translate-y-0'
            : 'opacity-0 invisible -translate-y-1'
        }`}
        style={{ backgroundColor: 'var(--vibe-color-background)' }}
      >
        {link.children!.map((child) => (
          <a
            key={child.href}
            href={child.href}
            className="oasis-nav-darken-hover block px-5 py-3 font-body text-[13px] font-semibold tracking-wider uppercase text-[var(--vibe-color-text)] transition-colors"
          >
            {child.label}
          </a>
        ))}
      </div>
    </div>
  );
}

// ============================================================================
// Desktop Navigation with dynamic "More" overflow dropdown
// ============================================================================

function DesktopNav({ navLinks }: { navLinks: NavLink[] }) {
  const navContainerRef = useRef<HTMLDivElement>(null);
  const measureRef = useRef<HTMLDivElement>(null);
  const [visibleCount, setVisibleCount] = useState(navLinks.length);
  const [moreOpen, setMoreOpen] = useState(false);
  const moreRef = useRef<HTMLDivElement>(null);

  const recalculate = useCallback(() => {
    const container = navContainerRef.current;
    const measure = measureRef.current;
    if (!container || !measure) return;

    const availableWidth = container.offsetWidth;
    const children = Array.from(measure.children) as HTMLElement[];
    const computedGap = parseFloat(getComputedStyle(container).gap) || 20;
    const moreButtonWidth = 90;

    let totalWidth = 0;
    let fitCount = 0;

    for (let i = 0; i < children.length; i++) {
      const childWidth = children[i].offsetWidth;
      const widthWithGap = i === 0 ? childWidth : childWidth + computedGap;
      const needed = totalWidth + widthWithGap + (i < children.length - 1 ? moreButtonWidth + computedGap : 0);

      if (needed > availableWidth && i < children.length) {
        const remainingWidth = totalWidth + widthWithGap;
        let allFit = true;
        let testWidth = remainingWidth;
        for (let j = i + 1; j < children.length; j++) {
          testWidth += children[j].offsetWidth + computedGap;
          if (testWidth > availableWidth) {
            allFit = false;
            break;
          }
        }
        if (allFit) {
          fitCount = children.length;
        } else {
          fitCount = i;
        }
        break;
      }
      totalWidth += widthWithGap;
      fitCount = i + 1;
    }

    setVisibleCount(fitCount);
  }, [navLinks.length]);

  useEffect(() => {
    recalculate();
    const observer = new ResizeObserver(() => recalculate());
    if (navContainerRef.current) {
      observer.observe(navContainerRef.current);
    }
    return () => observer.disconnect();
  }, [recalculate]);

  useEffect(() => {
    function handleClickOutside(e: MouseEvent) {
      if (moreRef.current && !moreRef.current.contains(e.target as Node)) {
        setMoreOpen(false);
      }
    }
    if (moreOpen) {
      document.addEventListener('mousedown', handleClickOutside);
      return () => document.removeEventListener('mousedown', handleClickOutside);
    }
  }, [moreOpen]);

  const visibleLinks = navLinks.slice(0, visibleCount);
  const overflowLinks = navLinks.slice(visibleCount);

  return (
    <>
      {/* Hidden measurement row. Parent links with children render with a
          chevron in the live nav, so we mirror that here so the per-link
          offsetWidth used by the overflow calculation matches the visible
          width — otherwise the chevron would push the rightmost link out
          unexpectedly when the row is near full. */}
      <div
        ref={measureRef}
        aria-hidden="true"
        className="flex items-center gap-5 absolute pointer-events-none"
        style={{ visibility: 'hidden', position: 'absolute', top: 0, left: 0, whiteSpace: 'nowrap' }}
      >
        {navLinks.map((link) => (
          <span
            key={link.href}
            className="font-body text-[13px] font-semibold tracking-wider uppercase inline-flex items-center gap-1"
          >
            {link.label}
            {link.children && link.children.length ? (
              <ChevronDownIcon className="w-3.5 h-3.5" />
            ) : null}
          </span>
        ))}
      </div>

      {/* Visible navigation */}
      {/* `justify-center` centers the link cluster within the flex-1
          container that sits between the logo and the (desktop-empty) right
          group. The desktop layout has no right-side content (the hamburger
          is mobile-only), so the nav container occupies all the space to
          the right of the logo — centering inside that container puts the
          links midway between the logo's right edge and the row's right
          edge, which reads as a centered cluster against the rest of the
          row. */}
      <nav
        ref={navContainerRef}
        className="hidden lg:flex items-center justify-center gap-5 xl:gap-7 flex-1 min-w-0 mx-6"
        role="navigation"
        aria-label="Main navigation"
      >
        {visibleLinks.map((link) => (
          <DesktopNavLink key={link.href} link={link} />
        ))}

        {overflowLinks.length > 0 && (
          <div
            ref={moreRef}
            className="relative"
            // Mirror the DesktopNavLink hover behavior — the "More"
            // overflow dropdown opens on hover/focus just like the
            // "Floor Plans" parent dropdown so the two affordances feel
            // identical. The click handler stays as a fallback for
            // touch devices where hover doesn't fire, and the
            // click-outside listener still dismisses the menu when the
            // pointer leaves the group entirely.
            onMouseEnter={() => setMoreOpen(true)}
            onMouseLeave={() => setMoreOpen(false)}
          >
            <button
              onClick={() => setMoreOpen(!moreOpen)}
              onFocus={() => setMoreOpen(true)}
              className="oasis-nav-darken-hover font-body text-[13px] font-semibold tracking-wider uppercase text-[var(--vibe-color-text)] transition-colors py-2 flex items-center gap-1 whitespace-nowrap"
              aria-expanded={moreOpen}
              aria-haspopup="true"
            >
              More
              <ChevronDownIcon className={`w-3.5 h-3.5 transition-transform duration-200 ${moreOpen ? 'rotate-180' : ''}`} />
            </button>

            <div
              className={`nav-more-dropdown absolute right-0 top-full mt-1 border border-[var(--vibe-color-border)] shadow-lg min-w-[200px] z-[70] transition-all duration-200 ${
                moreOpen
                  ? 'opacity-100 visible translate-y-0'
                  : 'opacity-0 invisible -translate-y-1'
              }`}
              style={{ backgroundColor: 'var(--vibe-color-background)' }}
            >
              {overflowLinks.map((link) => (
                <React.Fragment key={link.href}>
                  <a
                    href={link.href}
                    className="oasis-nav-darken-hover block px-5 py-3 font-body text-[13px] font-semibold tracking-wider uppercase text-[var(--vibe-color-text)] transition-colors"
                  >
                    {link.label}
                  </a>
                  {link.children?.map((child) => (
                    <a
                      key={child.href}
                      href={child.href}
                      // Indent child rows so the More dropdown reads as a
                      // nested list when a parent (e.g. Floor Plans) gets
                      // pushed into overflow and we want its sub-items to
                      // remain reachable from the dropdown.
                      className="oasis-nav-darken-hover block pl-9 pr-5 py-3 font-body text-[13px] font-semibold tracking-wider uppercase text-[var(--vibe-color-text-muted)] transition-colors"
                    >
                      {child.label}
                    </a>
                  ))}
                </React.Fragment>
              ))}
            </div>
          </div>
        )}
      </nav>
    </>
  );
}

// ============================================================================
// Mobile Nav Link
// ============================================================================

function MobileNavLink({
  link,
  onNavigate,
  isChild,
}: {
  link: NavLink;
  onNavigate: () => void;
  /** Drop-down (sub-nav) pages render distinctly from their parent: indented,
      in muted weight/color with a sentence-case (not uppercase) label and a
      leading guide marker. This preserves the parent→child hierarchy the
      desktop hover dropdown conveys, inside the drawer's vertical list. */
  isChild?: boolean;
}) {
  return (
    <a
      href={link.href}
      onClick={onNavigate}
      className={
        isChild
          ? 'oasis-nav-darken-hover flex items-center gap-2.5 py-3 pl-12 pr-6 font-body text-sm font-medium tracking-wide text-[var(--vibe-color-text-muted)] transition-colors border-b border-[var(--vibe-color-border)]/50'
          : 'oasis-nav-darken-hover block py-3.5 px-6 font-body text-sm font-semibold uppercase tracking-wider text-[var(--vibe-color-text)] transition-colors border-b border-[var(--vibe-color-border)]/50'
      }
    >
      {isChild && (
        <span
          aria-hidden="true"
          className="text-[var(--vibe-color-text-muted)]/60"
        >
          &#8627;
        </span>
      )}
      {link.label}
    </a>
  );
}

// ============================================================================
// Main Header Component
// ============================================================================
// STRUCTURE: Single navigation bar (logo | nav links | utility + CTA).
// The old 3-bar stack (banner + utility bar + nav bar) is replaced by one
// unified bar. Utility links (phone, Apply Now, Resident Access) are
// inlined into the right side of the nav bar on desktop, and folded into
// the mobile drawer on small screens.
// ============================================================================

export function Header() {
  const [menuOpen, setMenuOpen] = useState(false);
  const headerRef = useRef<HTMLElement>(null);

  // Header always renders solid: white background, black text. The previous
  // transparent-over-hero + scroll-to-solid behavior — and the luminance
  // picker that auto-tuned link/CTA colors for whatever primary brand color
  // sat behind the nav — were removed because the header's contract is now
  // "always white, always black". No page-context lookup, no tokens probing,
  // no scroll listener: just a static, legible bar.

  // Virtual tours — controls nav-link visibility
  const virtualTours = useCollectionBinding<VirtualTour>('header.virtual_tours', { collection: 'virtual_tours' });
  const hasVirtualTours = (virtualTours || []).some((t) => t.virtual_tour_url);

  // Interactive Map — drops out of the nav when the property has no
  // ApartmentSync (Repli360 / RRAC) IDs to drive the "interactive
  // leasing view" widget on that page. We require BOTH the JavaScript
  // ID (script URL token) and the Config ID (site identifier) — a
  // property with only one populated is a half-configured state and
  // shouldn't show the page (e.g. Centre Point, which has neither, was
  // leaking into the nav when only the JavaScript ID was checked).
  // Mirrors the SSG-side hide + noindex logic in ssg-entry.ts so
  // navigation, sitemap, and crawler hints stay consistent. The fields
  // are custom property fields (typed as `text`) so we read them via
  // `useProperty()` directly rather than the strongly-typed
  // `usePropertyField` (which only knows the built-in scalar fields).
  const propertyForLeasing = useProperty() as Record<string, unknown>;
  const apartmentSyncJsId = propertyForLeasing?.custom_apartmentsync_javascript_id as
    | string
    | null
    | undefined;
  const apartmentSyncConfigId = propertyForLeasing?.custom_apartmentsync_config_id as
    | string
    | null
    | undefined;
  const hasInteractiveLeasing =
    !!(apartmentSyncJsId && String(apartmentSyncJsId).trim()) &&
    !!(apartmentSyncConfigId && String(apartmentSyncConfigId).trim());

  const navLinks = useMemo(
    () => buildNavLinks(hasVirtualTours, hasInteractiveLeasing),
    [hasVirtualTours, hasInteractiveLeasing],
  );

  // Content tokens
  const phone = useContentToken('header.phone');

  // Slot groups
  const logoRaw = useSlotGroup('header.logo', logoSlotDef);
  const monoWhiteLogo = usePropertyField('property_mono_white_logo');
  const monoBlackLogo = usePropertyField('property_mono_black_logo');

  // The header always sits on a white background, so a previously-saved
  // mono_white logo selection would be invisible. Auto-swap it to the
  // mono_black equivalent when available; otherwise honor whatever the
  // editor picked.
  const logoImage =
    logoRaw.image && monoWhiteLogo && logoRaw.image === monoWhiteLogo && monoBlackLogo
      ? monoBlackLogo
      : logoRaw.image;
  const logo = { ...logoRaw, image: logoImage };

  const applyNowLink = useSlotGroup('header.apply_now_link', applyNowLinkDef);
  const residentAccessLink = useSlotGroup('header.resident_access_link', residentAccessLinkDef);
  const scheduleTourLink = useSlotGroup('header.schedule_tour_link', scheduleTourLinkDef);
  const addressLink = useSlotGroup('header.address_link', addressLinkDef);
  const viewFloorPlansLink = useSlotGroup('header.view_floor_plans_link', viewFloorPlansLinkDef);

  // Social icons that ride next to the phone in the desktop utility row.
  // Each one renders only when its URL resolves — typically from the
  // matching property field — so the row stays uncluttered for properties
  // that don't run a given channel.
  const headerFacebook = useSlotGroup('header.facebook_link', headerFacebookLinkDef);
  const headerTwitter = useSlotGroup('header.twitter_link', headerTwitterLinkDef);
  const headerGoogle = useSlotGroup('header.google_link', headerGoogleLinkDef);

  // Google Fonts
  useEffect(() => {
    // Reconciliation: nuke any prior `data-vibe-font` link (preconnect
    // hints + stylesheets) before injecting, then inject. Earlier
    // revisions of this file used an early-return guard so the injection
    // only happened once per session — but live-preview / HMR sessions
    // retain DOM across reloads, which meant the previous `Nunito`
    // stylesheet stayed pinned to the head and the new `Nunito Sans`
    // family (matching https://www.lakehausapartmenthomes.com/ ) never
    // loaded. Replacing instead of guarding makes every Header mount
    // fully reconcile the font links to the current source.
    document.querySelectorAll('link[data-vibe-font]').forEach((el) => el.remove());

    const preconnect1 = document.createElement('link');
    preconnect1.rel = 'preconnect';
    preconnect1.href = 'https://fonts.googleapis.com';
    preconnect1.setAttribute('data-vibe-font', 'preconnect-gfonts');
    document.head.appendChild(preconnect1);
    const preconnect2 = document.createElement('link');
    preconnect2.rel = 'preconnect';
    preconnect2.href = 'https://fonts.gstatic.com';
    preconnect2.crossOrigin = 'anonymous';
    preconnect2.setAttribute('data-vibe-font', 'preconnect-gstatic');
    document.head.appendChild(preconnect2);
    const link = document.createElement('link');
    link.rel = 'stylesheet';
    // Playfair Display (heading) + Nunito Sans (body) — body family
    // updated from `Nunito` to `Nunito Sans` to match the typographic
    // treatment used at https://www.lakehausapartmenthomes.com/ , which
    // ships its body copy in Nunito Sans 400 at 16px with a roomy
    // line-height of 1.92 (the matching global line-height rule lives
    // in the <style> block below).
    link.href = 'https://fonts.googleapis.com/css2?family=Playfair+Display:wght@400;500;600;700;800&family=Nunito+Sans:wght@400;500;600;700&display=swap';
    link.setAttribute('data-vibe-font', 'theme-fonts');
    document.head.appendChild(link);
  }, []);

  // Lock body scroll when mobile menu is open
  useEffect(() => {
    document.body.style.overflow = menuOpen ? 'hidden' : '';
    return () => { document.body.style.overflow = ''; };
  }, [menuOpen]);

  const closeMobileMenu = () => setMenuOpen(false);

  return (
    <>
      {/* Google Fonts — rendered in JSX (not just via the useEffect
          above) so the stylesheet ships during SSR and during the
          first paint before any client-side effect runs. The
          useEffect still reconciles links across re-renders for live-
          preview / HMR; this JSX render ensures static / SSR pages
          also receive the fonts. \`Nunito Sans\` (body) is matched to
          the typographic treatment at
          https://www.lakehausapartmenthomes.com/ , Playfair Display
          stays the heading family. */}
      <link rel="preconnect" href="https://fonts.googleapis.com" />
      <link rel="preconnect" href="https://fonts.gstatic.com" crossOrigin="anonymous" />
      <link
        rel="stylesheet"
        href="https://fonts.googleapis.com/css2?family=Playfair+Display:wght@400;500;600;700;800&family=Nunito+Sans:wght@400;500;600;700&display=swap"
      />

      {/* No spacer — transparent header overlays hero content */}

      <header
        ref={headerRef}
        className="fixed left-0 right-0 z-50 w-full"
        style={{
          top: 0,
          // Offer-banner offset so both the utility row and the main nav row
          // shift down together when the banner is visible.
          paddingTop: 'var(--offer-banner-height, 0px)',
          transition: 'padding-top 0.5s cubic-bezier(0.4, 0, 0.2, 1)',
          backgroundColor: 'var(--vibe-color-background)',
        }}
      >
        {/* ============================================================== */}
        {/* Utility row — sits above the main nav, desktop-only            */}
        {/* ---------------------------------------------------------------- */}
        {/* Uses the same primary / text-on-primary palette as the footer  */}
        {/* so the chrome at the very top of the page mirrors the chrome   */}
        {/* at the bottom. Mobile parity for these items lives in the      */}
        {/* mobile menu drawer below.                                      */}
        {/* ============================================================== */}
        <div className="header-utility-row hidden lg:block">
          <div className="px-4 sm:px-6 lg:px-8">
            <div className="flex items-center justify-between gap-3 py-2.5">
              {/* Left cluster — phone + social icons */}
              <div className="flex items-center gap-2">
                {/* Phone */}
                <a
                  href={`tel:${phone?.replace(/[^+\d]/g, '')}`}
                  className="dniphone dniphonehref utility-row-link"
                  aria-label="Call us"
                >
                  <PhoneIcon className="w-3.5 h-3.5 flex-shrink-0" />
                  <EditableToken
                    path="header.phone"
                    defaultField="property_call_tracking_phone"
                    defaultValue="(555) 555-5555"
                    as="span"
                  >
                    {formatPhone(phone)}
                  </EditableToken>
                </a>

                {/* Social icon sub-cluster — kept tight (gap-0) so the icons
                    read as a single visual group, while the parent gap-2
                    above preserves a comfortable separation between the
                    phone link and the first icon. */}
                <div className="flex items-center gap-0">
                  {/* Facebook */}
                  {headerFacebook.url && (
                    <EditableSlotGroup groupPath="header.facebook_link" definition={headerFacebookLinkDef}>
                      <a
                        href={headerFacebook.url}
                        target={headerFacebook._target || '_blank'}
                        rel="noopener noreferrer"
                        aria-label={headerFacebook.label || 'Facebook'}
                        className="utility-row-social"
                      >
                        <FontAwesomeIcon icon={faFacebook} className="w-3.5 h-3.5" />
                      </a>
                    </EditableSlotGroup>
                  )}

                  {/* Twitter / X */}
                  {headerTwitter.url && (
                    <EditableSlotGroup groupPath="header.twitter_link" definition={headerTwitterLinkDef}>
                      <a
                        href={headerTwitter.url}
                        target={headerTwitter._target || '_blank'}
                        rel="noopener noreferrer"
                        aria-label={headerTwitter.label || 'Twitter'}
                        className="utility-row-social"
                      >
                        <FontAwesomeIcon icon={faXTwitter} className="w-3.5 h-3.5" />
                      </a>
                    </EditableSlotGroup>
                  )}

                  {/* Google Business */}
                  {headerGoogle.url && (
                    <EditableSlotGroup groupPath="header.google_link" definition={headerGoogleLinkDef}>
                      <a
                        href={headerGoogle.url}
                        target={headerGoogle._target || '_blank'}
                        rel="noopener noreferrer"
                        aria-label={headerGoogle.label || 'Google'}
                        className="utility-row-social"
                      >
                        <FontAwesomeIcon icon={faGoogle} className="w-3.5 h-3.5" />
                      </a>
                    </EditableSlotGroup>
                  )}
                </div>
              </div>

              {/* Right cluster — quick links + CTA */}
              <div className="flex items-center gap-3">
                {/* Residents — paired with a two-person "group" icon
                    (matching the reference demo's `Residents-1920w.png`
                    glyph, which is a pair of head-and-shoulder
                    silhouettes). Placed first in the right cluster
                    (ahead of Apply Now) because the resident portal is
                    the higher-frequency action for the existing-tenant
                    audience that lives in the utility row. Icon
                    precedes the label so it aligns with the phone icon
                    on the opposite side of the bar. */}
                <EditableSlotGroup groupPath="header.resident_access_link" definition={residentAccessLinkDef}>
                  <a
                    href={residentAccessLink.url || '/resident-access'}
                    target={residentAccessLink._target || '_self'}
                    className="utility-row-link"
                  >
                    <FontAwesomeIcon icon={faUsers} className="w-3.5 h-3.5 flex-shrink-0" aria-hidden="true" />
                    {residentAccessLink.label || 'Residents'}
                  </a>
                </EditableSlotGroup>

                <span className="utility-row-divider" aria-hidden="true" />

                {/* Apply Now — paired with a handshake icon so the link
                    frames the lease as an agreement / "let's sign on"
                    moment rather than a paperwork task. Same
                    icon-before-text pattern as the Residents link so the
                    two utility links read as a consistent pair. */}
                <EditableSlotGroup groupPath="header.apply_now_link" definition={applyNowLinkDef}>
                  <a
                    href={applyNowLink.url || '/apply-now'}
                    target={applyNowLink._target || '_self'}
                    className="utility-row-link"
                  >
                    <FontAwesomeIcon icon={faHandshake} className="w-3.5 h-3.5 flex-shrink-0" aria-hidden="true" />
                    {applyNowLink.label || 'Apply Now'}
                  </a>
                </EditableSlotGroup>
              </div>
            </div>
          </div>
        </div>

        {/* ============================================================== */}
        {/* Single unified navigation bar                                  */}
        {/* ============================================================== */}
        <div className="header-bar">
          <div className="px-4 sm:px-6 lg:px-8">
            <div className="flex items-center h-24 lg:h-28">
              {/* Logo */}
              {/* Wrapper carries the explicit logo slot height
                  (h-16 / md:h-20 / lg:h-20) AND `overflow-hidden`. The image
                  inside uses `max-h-full` so its rendered height is hard-capped
                  to the wrapper regardless of the asset's intrinsic aspect
                  ratio, and any overshoot — tall portrait crests, decorative
                  flourishes, etc. — is clipped rather than allowed to extend
                  past the header bar. Before this fix the logo could bleed
                  below the `h-24` / `h-32` nav row, which on mobile meant the
                  asset visually ran underneath the hamburger drawer the
                  moment it opened (the drawer's top is anchored to the
                  header's bottom).
                  Mobile (default) was bumped from h-14 → h-16 and a new
                  md: step was added at h-20 so tablets get a noticeably
                  larger logo than phones (previously both fell under the lg
                  breakpoint and shared the same 56px size). The mobile bar
                  height stays h-24 (96px) so the drawer-top calc below —
                  which hard-codes `6rem` — remains in sync. */}
              <EditableSlotGroup groupPath="header.logo" definition={logoSlotDef}>
                <a
                  href="/"
                  className="flex-shrink-0 flex items-center h-16 md:h-20 lg:h-20 overflow-hidden"
                >
                  {logo.image ? (
                    <VibesitesImage
                      url={logo.image}
                      alt={logo.alt || 'Community Logo'}
                      className="max-h-full w-auto object-contain"
                    />
                  ) : (
                    <div className="font-heading text-xl lg:text-2xl font-bold text-[var(--vibe-color-primary)]">
                      The Oasis
                    </div>
                  )}
                </a>
              </EditableSlotGroup>

              {/* Desktop Navigation Links */}
              <DesktopNav navLinks={navLinks} />

              {/* Right group — desktop Schedule-a-Tour CTA + mobile        */}
              {/* hamburger. Phone, Apply Now, and Residents live in the    */}
              {/* primary-colored utility row above the bar. The Schedule a */}
              {/* Tour CTA sits here instead so the main nav row carries    */}
              {/* the primary conversion action against a white background. */}
              <div className="flex items-center gap-3 flex-shrink-0 ml-auto">
                {/* Schedule a Tour CTA — desktop only. Mobile parity for
                    this action lives inside the open hamburger drawer
                    further down so the small-viewport entry point stays
                    consistent. */}
                <EditableSlotGroup groupPath="header.schedule_tour_link" definition={scheduleTourLinkDef}>
                  <a
                    href={scheduleTourLink.url || '/schedule-a-tour'}
                    target={scheduleTourLink._target || '_self'}
                    className="header-nav-cta hidden lg:inline-flex"
                  >
                    {scheduleTourLink.label || 'Schedule a Tour'}
                  </a>
                </EditableSlotGroup>

                {/* Mobile Hamburger */}
                <button
                  onClick={() => setMenuOpen(!menuOpen)}
                  className="lg:hidden relative w-10 h-10 flex items-center justify-center"
                  aria-label={menuOpen ? 'Close menu' : 'Open menu'}
                  aria-expanded={menuOpen}
                >
                  <span
                    className={`absolute block w-6 h-0.5 bg-[var(--vibe-color-text)] transition-all duration-300 ${
                      menuOpen ? 'rotate-45' : '-translate-y-2'
                    }`}
                  />
                  <span
                    className={`absolute block w-6 h-0.5 bg-[var(--vibe-color-text)] transition-all duration-300 ${
                      menuOpen ? 'opacity-0' : ''
                    }`}
                  />
                  <span
                    className={`absolute block w-6 h-0.5 bg-[var(--vibe-color-text)] transition-all duration-300 ${
                      menuOpen ? '-rotate-45' : 'translate-y-2'
                    }`}
                  />
                </button>
              </div>
            </div>
          </div>
        </div>

        {/* ============================================================== */}
        {/* Mobile Menu Overlay                                            */}
        {/* ============================================================== */}
        {/* Drawer top mirrors the header's own vertical layout in CSS so it
            is always anchored to the header bar's bottom edge, never lagging
            a frame behind the offer banner expanding/collapsing.
            - `--offer-banner-height` is the same variable the SpecialOffer
              banner publishes (set on :root) and that the header consumes as
              its `padding-top`, so the drawer shifts down in lock-step with
              the banner.
            - `6rem` is the mobile / tablet height of the main nav row
              (`flex items-center h-24` → 96px). The drawer is `lg:hidden`,
              so the desktop `lg:h-32` value never applies here.
            Switching from a JS-measured `headerHeight` removed a class of
            bugs where the logo appeared to slip under the drawer on first
            paint (state hadn't caught up to a freshly-shown banner via the
            ResizeObserver). */}
        <div
          className={`mobile-menu-panel lg:hidden fixed inset-x-0 bottom-0 overflow-y-auto transition-all duration-300 z-[61] ${
            menuOpen
              ? 'opacity-100 visible'
              : 'opacity-0 invisible pointer-events-none'
          }`}
          style={{
            top: 'calc(var(--offer-banner-height, 0px) + 6rem)',
            backgroundColor: 'var(--vibe-color-background)',
          }}
        >
          {/* Navigation links */}
          <nav role="navigation" aria-label="Mobile navigation">
            {navLinks.map((link) => (
              <React.Fragment key={link.href}>
                <MobileNavLink link={link} onNavigate={closeMobileMenu} />
                {link.children?.map((child) => (
                  <MobileNavLink
                    key={child.href}
                    link={child}
                    onNavigate={closeMobileMenu}
                    isChild
                  />
                ))}
              </React.Fragment>
            ))}
          </nav>

          {/* Schedule a Tour CTA — moved here from the (now removed) top
              header-bar CTA so the mobile drawer remains the entry point
              for that action on small viewports. Uses the same
              `mobile-util-pill` outline-fill-on-hover treatment as the
              View Floor Plans / Apply Now / Resident Access pills so all
              drawer CTAs share a single visual language. */}
          <div className="px-5 pt-5 pb-2">
            <EditableSlotGroup groupPath="header.schedule_tour_link" definition={scheduleTourLinkDef}>
              <a
                href={scheduleTourLink.url || '/schedule-a-tour'}
                target={scheduleTourLink._target || '_self'}
                onClick={closeMobileMenu}
                className="mobile-util-pill block text-center w-full py-3 font-heading text-base font-medium tracking-[0.15em] uppercase border border-[var(--vibe-color-primary)] text-[var(--vibe-color-primary)] hover:bg-[var(--vibe-color-primary)] transition-all"
              >
                {scheduleTourLink.label || 'Schedule a Tour'}
              </a>
            </EditableSlotGroup>
          </div>

          {/* View Floor Plans CTA — secondary primary action below the
              Schedule a Tour pill. Same outline-fill-on-hover treatment.
              Wrapped in EditableSlotGroup so the editor can target it from
              inside the open drawer (url / label / target). */}
          <div className="px-5 pb-3">
            <EditableSlotGroup groupPath="header.view_floor_plans_link" definition={viewFloorPlansLinkDef}>
              <a
                href={viewFloorPlansLink.url || '/floor-plans'}
                target={viewFloorPlansLink._target || '_self'}
                onClick={closeMobileMenu}
                className="mobile-util-pill block text-center w-full py-3.5 text-sm font-medium tracking-[0.15em] uppercase border border-[var(--vibe-color-primary)] text-[var(--vibe-color-primary)] hover:bg-[var(--vibe-color-primary)] transition-all"
              >
                {viewFloorPlansLink.label || 'View Floor Plans'}
              </a>
            </EditableSlotGroup>
          </div>

          {/* Utility CTA Buttons — wrapped in EditableSlotGroup so the
              editor can target them from inside the open mobile drawer
              the same way it can in the desktop nav above. The wrappers
              are display:contents-ish in practice (they don't introduce
              visible chrome), so the two-column grid layout below still
              treats each anchor as a direct grid item. */}
          <div className="px-5 pb-3">
            <div className="grid grid-cols-2 gap-2">
              {/* Residents first to mirror the desktop utility-row order
                  (Residents → Apply Now) so the two layouts share the same
                  reading sequence on every viewport. */}
              <EditableSlotGroup groupPath="header.resident_access_link" definition={residentAccessLinkDef}>
                <a
                  href={residentAccessLink.url || '/resident-access'}
                  target={residentAccessLink._target || '_self'}
                  className="mobile-util-pill block text-center py-2.5 text-[11px] font-medium tracking-wider uppercase border border-[var(--vibe-color-primary)] text-[var(--vibe-color-primary)] hover:bg-[var(--vibe-color-primary)] transition-all"
                >
                  {residentAccessLink.label || 'Residents'}
                </a>
              </EditableSlotGroup>
              <EditableSlotGroup groupPath="header.apply_now_link" definition={applyNowLinkDef}>
                <a
                  href={applyNowLink.url || '/apply-now'}
                  target={applyNowLink._target || '_self'}
                  className="mobile-util-pill block text-center py-2.5 text-[11px] font-medium tracking-wider uppercase border border-[var(--vibe-color-primary)] text-[var(--vibe-color-primary)] hover:bg-[var(--vibe-color-primary)] transition-all"
                >
                  {applyNowLink.label || 'Apply Now'}
                </a>
              </EditableSlotGroup>
            </div>
          </div>

          {/* Address & Phone */}
          <div className="px-5 pb-6 pt-3 border-t border-[var(--vibe-color-border)]/50">
            <div className="space-y-3">
              <EditableSlotGroup groupPath="header.address_link" definition={addressLinkDef}>
                <a
                  href={addressLink.url || '#'}
                  target={addressLink._target || '_blank'}
                  rel="noopener noreferrer"
                  className="flex items-center gap-2.5 text-sm text-[var(--vibe-color-text-muted)] hover:text-[var(--vibe-color-primary)] transition-colors"
                >
                  <MapPinIcon className="w-4 h-4 text-[var(--vibe-color-primary)] flex-shrink-0" />
                  <span className="oasis-nav-darken-hover">{addressLink.address}</span>
                </a>
              </EditableSlotGroup>
              <a
                href={`tel:${phone?.replace(/[^+\d]/g, '')}`}
                className="dniphone dniphonehref flex items-center gap-2.5 text-sm text-[var(--vibe-color-text-muted)] hover:text-[var(--vibe-color-primary)] transition-colors"
              >
                <PhoneIcon className="w-4 h-4 text-[var(--vibe-color-primary)] flex-shrink-0" />
                {/* Mirror the desktop phone treatment: EditableToken
                    wraps just the inner text span so the editor can
                    target the phone number from inside the open mobile
                    drawer. The outer <a> still routes to `tel:` for
                    real visitors. */}
                <EditableToken
                  path="header.phone"
                  defaultField="property_call_tracking_phone"
                  defaultValue="(555) 555-5555"
                  as="span"
                  className="oasis-nav-darken-hover"
                >
                  {formatPhone(phone)}
                </EditableToken>
              </a>
            </div>
          </div>
        </div>
      </header>

      <style>{`
        /* ================================================================ */
        /* Global body typography                                          */
        /* ---------------------------------------------------------------- */
        /* Body font + line-height locked to the typographic treatment at
           https://www.lakehausapartmenthomes.com/ , whose global rule
           reads \`line-height: 1.92 !important\` on every paragraph in
           Nunito Sans 400.

           \`font-family\` on body is hard-pinned to \`Nunito Sans\` with
           !important rather than relying solely on the
           \`--vibe-font-body\` CSS variable. In live-preview / HMR
           sessions a stale VibesitesProvider state can keep the
           variable pointing at the previous \`Nunito\` value until the
           provider remounts, which makes the swap invisible to the
           user even though \`src/tokens.ts\` was updated. The explicit
           family name short-circuits that lag while still falling
           through to the variable as a future-proof secondary so any
           later token override (e.g. a per-property design override
           that sets \`font.body\` to something else) still wins via
           cascade-after-this-rule.

           Per-component \`leading-*\` Tailwind classes still beat body
           line-height (they're applied to descendants), so headings
           keep their tight measure. Heading family also stays correct
           because every heading element sets \`font-family:
           var(--vibe-font-heading)\` via the \`font-heading\` Tailwind
           utility, which beats body inheritance. */
        /* ================================================================ */
        body {
          font-family: 'Nunito Sans', var(--vibe-font-body), system-ui, -apple-system, sans-serif !important;
          line-height: 1.92;
        }

        /* Force the Tailwind \`.font-body\` utility — used on 143
           elements across the theme — to resolve to \`Nunito Sans\`
           directly instead of via \`var(--vibe-font-body)\`. The
           variable is normally the right path (it lets per-property
           token overrides flow through), but in live-preview / HMR
           sessions the VibesitesProvider can hold a cached
           themeModule whose tokens still list the old \`Nunito\`
           family, which would keep every \`font-body\` element
           rendering in Nunito even after \`src/tokens.ts\` was edited.
           Pinning the resolved family inside the Tailwind utility
           short-circuits that. \`var(--vibe-font-body)\` stays in the
           stack as a future-proof fallback so any later per-token
           override that explicitly sets a different body family still
           wins (it'd come from a more-specific selector / inline
           style and beat this class-level rule). */
        .font-body {
          font-family: 'Nunito Sans', var(--vibe-font-body), system-ui, -apple-system, sans-serif !important;
        }

        /* ================================================================ */
        /* Utility row (above the main nav)                                */
        /* ---------------------------------------------------------------- */
        /* Sits at the very top of the fixed header on desktop. Painted in
           --vibe-color-primary with --vibe-color-text-on-primary labels so
           the band reads as branded chrome — the inverse of the white main
           nav below — and earns visual weight against the page content.
           The runtime keeps text-on-primary WCAG-calibrated to whatever
           the primary brand color is, so the row stays legible across
           every palette (on this theme it resolves to #FFFFFF, matching
           the "white" requested either way). Always renders solid (no
           transparent-over-hero variant) so the items stay readable
           regardless of what section the nav overlays.                    */
        /* ================================================================ */
        .header-utility-row {
          background-color: var(--vibe-color-primary);
          color: var(--vibe-color-text-on-primary);
        }

        .utility-row-link {
          display: inline-flex;
          align-items: center;
          gap: 0.4rem;
          padding: 0.25rem 0.25rem;
          font-family: var(--vibe-font-body);
          /* Bumped from 0.72rem to 0.78rem and de-capped — without the
             uppercase + 0.08em tracking these labels read smaller, so a
             gentle size bump keeps the row visually balanced. */
          font-size: 0.78rem;
          font-weight: 600;
          letter-spacing: 0.01em;
          /* On the primary band the label always renders as
             --vibe-color-text-on-primary; the resting state dims to 85%
             opacity so hover (snap to 100%) still reads as a clear
             interaction. Using opacity (rather than a separate muted
             color token) means the label always tracks whatever
             text-on-primary the runtime resolves for the current
             palette. */
          color: var(--vibe-color-text-on-primary);
          opacity: 0.85;
          text-decoration: none;
          white-space: nowrap;
          transition: opacity 0.2s ease;
        }
        .utility-row-link:hover {
          opacity: 1;
        }

        .utility-row-divider {
          display: block;
          width: 1px;
          height: 14px;
          /* Divider rides the primary band, so derive it from
             text-on-primary at low opacity rather than the border token
             (which is calibrated against the white nav background and
             would disappear here). */
          background: color-mix(in srgb, var(--vibe-color-text-on-primary) 35%, transparent);
          flex-shrink: 0;
        }

        /* Social icons in the left cluster of the utility row. Mirror the
           .utility-row-link opacity treatment so icons and labels animate
           together on hover. */
        .utility-row-social {
          display: inline-flex;
          align-items: center;
          justify-content: center;
          width: 1.5rem;
          height: 1.5rem;
          border-radius: 9999px;
          color: var(--vibe-color-text-on-primary);
          opacity: 0.85;
          text-decoration: none;
          transition: opacity 0.2s ease, background-color 0.2s ease;
        }
        .utility-row-social:hover {
          opacity: 1;
          background-color: color-mix(in srgb, var(--vibe-color-text-on-primary) 14%, transparent);
        }

        /* ================================================================ */
        /* Schedule-a-Tour CTA in the main nav bar (desktop only)          */
        /* ---------------------------------------------------------------- */
        /* Moved down from the primary-band utility row into the white nav
           bar so the primary conversion action sits inline with the nav
           links rather than at the top of the page chrome.
           Palette: secondary fill at rest, primary on hover — the inverse
           of the hero section CTA so the two main conversion buttons on
           the home page read as a complementary pair (hero leads with
           primary, header leads with secondary).
           - Default fill = --vibe-color-secondary, label =
             --vibe-color-text-on-secondary (runtime-calibrated for WCAG).
           - No border (borderless pill).
           - Hover fill = --vibe-color-primary, label flips to
             --vibe-color-text-on-primary so the swap stays readable
             regardless of how dark/light the primary brand color is.
           The element wears Tailwind's "hidden lg:inline-flex" so it
           collapses on mobile and lays out as a flex pill on desktop —
           we leave align-items / gap on the class here for that lg
           state. */
        /* ================================================================ */
        .header-nav-cta {
          align-items: center;
          gap: 0.5rem;
          /* Sized to match the hero section CTA (.hero-cta) — padding
             1rem / 2.5rem, font-size 0.875rem (text-sm), letter-spacing
             0.05em — so the two main conversion buttons on the home
             page read as a matched pair in both palette (inverse) and
             physical proportions. Font-weight stays at 600 (vs the
             hero's 500) so the label still pairs cleanly with the
             font-semibold nav links sitting beside it in the bar. */
          padding: 0.875rem 2.5rem;
          background-color: var(--vibe-color-secondary);
          border: none;
          border-radius: 2px;
          color: var(--vibe-color-text-on-secondary);
          font-family: var(--vibe-font-heading);
          font-size: 1rem;
          font-weight: 600;
          letter-spacing: 0.05em;
          text-decoration: none;
          white-space: nowrap;
          transition: background-color 0.25s ease, color 0.25s ease, transform 0.25s ease;
        }
        .header-nav-cta:hover {
          background-color: var(--vibe-color-primary);
          color: var(--vibe-color-text-on-primary);
          transform: translateY(-1px);
        }
        .header-nav-cta:active {
          transform: translateY(0);
        }

        /* ================================================================ */
        /* Single navigation bar                                           */
        /* ---------------------------------------------------------------- */
        /* The header always renders on a white background with black text,
           so there are no transparent / scrolled / over-hero variants here —
           just one steady visual state.                                    */
        /* ================================================================ */
        .header-bar {
          border-bottom: 1px solid transparent;
        }

        /* Mobile menu panel uses --vibe-color-background (white). Hover
           behavior for its links is driven by the .oasis-nav-darken-hover
           rule below (color flip to --vibe-color-secondary), so we hold
           the base link color at the theme text color here. */
        .mobile-menu-panel a,
        .mobile-menu-panel button {
          color: var(--vibe-color-text);
        }

        /* Nav links (DesktopNavLink, "More" dropdown items, MobileNavLink,
           plus the mobile drawer address/phone rows that wear this class on
           an inner span). Hover replaces the previous animated-underline
           treatment with a straight color flip to --vibe-color-secondary,
           per request. */
        .oasis-nav-darken-hover {
          transition: color 0.2s ease;
        }
        .oasis-nav-darken-hover:hover {
          color: var(--vibe-color-secondary);
        }
        /* The mobile drawer address / phone rows attach the class to an
           inner <span>, so a hover on the parent <a> never lights the span
           up directly. Trigger the same color flip from the parent's
           :hover / :focus-visible so the whole row stays the hover target. */
        .mobile-menu-panel a:hover .oasis-nav-darken-hover,
        .mobile-menu-panel a:focus-visible .oasis-nav-darken-hover {
          color: var(--vibe-color-secondary);
        }
        /* Outline pills inside the drawer flip to text-on-primary on hover. */
        .mobile-menu-panel .mobile-util-pill:hover {
          color: var(--vibe-color-text-on-primary);
        }

      `}</style>
    </>
  );
}

// ============================================================================
// Content Schema
// ============================================================================

export const contentSchema: ComponentContentSchema = {
  slot: 'Header',
  displayName: 'Header',
  tokens: {
    'header.phone': {
      label: 'Phone Number',
      type: 'phone',
      defaultField: 'property_call_tracking_phone',
      defaultValue: '(555) 555-5555',
    },
  },
  slotGroups: {
    'header.logo': {
      label: 'Logo',
      fields: {
        image: { label: 'Logo Image', type: 'image', defaultField: 'property_mono_black_logo', suggestedFields: ['property_mono_black_logo', 'property_primary_logo', 'property_secondary_logo'] },
        alt: { label: 'Alt Text', type: 'text', defaultValue: 'Community Logo' },
      },
    },
    'header.apply_now_link': {
      label: 'Apply Now Link',
      fields: {
        url: {
          label: 'URL',
          type: 'url',
          defaultField: 'property_apply_now_url',
          suggestedFields: ['property_apply_now_url', 'property_website_url'],
        },
        label: { label: 'Link Text', type: 'text', defaultValue: 'Apply Now' },
        _target: { label: 'Target', type: 'text', defaultValue: '_self' },
      },
    },
    'header.resident_access_link': {
      label: 'Residents Link',
      fields: {
        url: { label: 'URL', type: 'url', defaultValue: '/resident-access' },
        label: { label: 'Link Text', type: 'text', defaultValue: 'Residents' },
        _target: { label: 'Target', type: 'text', defaultValue: '_self' },
      },
    },
    'header.schedule_tour_link': {
      label: 'Schedule Tour Link',
      fields: {
        url: { label: 'URL', type: 'url', defaultValue: '/schedule-a-tour' },
        label: { label: 'Link Text', type: 'text', defaultValue: 'Schedule a Tour' },
        _target: { label: 'Target', type: 'text', defaultValue: '_self' },
      },
    },
    'header.address_link': {
      label: 'Address Link',
      fields: {
        address: {
          label: 'Address',
          type: 'text',
          defaultField: 'property_full_address',
          defaultValue: '123 Main Street, Atlanta, GA 30313',
        },
        url: {
          label: 'URL',
          type: 'url',
          defaultField: 'property_google_place_url',
          suggestedFields: ['property_google_place_url'],
        },
        _target: { label: 'Target', type: 'text', defaultValue: '_blank' },
      },
    },
    'header.view_floor_plans_link': {
      label: 'View Floor Plans Link',
      fields: {
        url: { label: 'URL', type: 'url', defaultValue: '/floor-plans' },
        label: { label: 'Link Text', type: 'text', defaultValue: 'View Floor Plans' },
        _target: { label: 'Target', type: 'text', defaultValue: '_self' },
      },
    },
    'header.facebook_link': {
      label: 'Facebook',
      fields: {
        url: {
          label: 'URL',
          type: 'url',
          defaultField: 'property_facebook_url',
          suggestedFields: ['property_facebook_url'],
        },
        label: { label: 'Label', type: 'text', defaultValue: 'Facebook' },
        _target: { label: 'Target', type: 'text', defaultValue: '_blank' },
      },
    },
    'header.twitter_link': {
      label: 'Twitter / X',
      fields: {
        url: {
          label: 'URL',
          type: 'url',
          defaultField: 'property_twitter_url',
          suggestedFields: ['property_twitter_url'],
        },
        label: { label: 'Label', type: 'text', defaultValue: 'Twitter' },
        _target: { label: 'Target', type: 'text', defaultValue: '_blank' },
      },
    },
    'header.google_link': {
      label: 'Google Business',
      fields: {
        url: {
          label: 'URL',
          type: 'url',
          defaultField: 'property_google_business_url',
          suggestedFields: ['property_google_business_url'],
        },
        label: { label: 'Label', type: 'text', defaultValue: 'Google' },
        _target: { label: 'Target', type: 'text', defaultValue: '_blank' },
      },
    },
  },
  collectionBindings: {
    'header.virtual_tours': {
      label: 'Virtual Tours',
      collection: 'virtual_tours',
    },
  },
};

(Header as any).contentSchema = contentSchema;

export default Header;
